import { useState, useEffect } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import ToggleButton from './ToggleButton';
import HotWaterRatioInput, { HOT_WATER_SOURCE_LABELS } from './HotWaterRatioInput';
import { getHotWaterRatioWithSource } from '../../clientPages/actionPlan/calculations/waterPricing';
import UsageCategoryPicker from '../../shared/UsageCategoryPicker';
import { usageHasSurface } from './utils';
import FunctionalUnitTag from '../../shared/FunctionalUnitTag';
import metadata from '../../../metadata.json';

// Robinet/douche/machine : daily_usage est un temps (min/jour) si l'équipement
// est réglé en débit (L/min) — le reste (WC, urinoir, ou une machine en
// L/cycle) compte des activations (cycles/jour). Sert de repli tant que
// l'unité réelle de l'équipement (waterValueUnit) n'est pas connue.
const TIME_BASED_TYPES = ['faucet', 'shower'];

// Aperçu en direct du volume d'activations annuel, à partir des mêmes règles
// que consumptionCalculations.js (nbReelWeeks pondéré par les semaines
// d'activité intense/réduite) — mais exprimé en minutes/cycles, pas en litres,
// donc calculé séparément plutôt que réutilisé depuis ce module.
function computeUsageStats(isTimeBased, usageForm) {
    const { daily_usage, daily_usage_type, nb_users, weekly_usage, yearly_usage, less_intense_weeks, more_intense_weeks } = usageForm.usage_data;
    if (!daily_usage || !weekly_usage || !yearly_usage) return null;

    const lessWeeks = less_intense_weeks || 0;
    const moreWeeks = more_intense_weeks || 0;
    const nbReelWeeks = (yearly_usage - lessWeeks - moreWeeks)
        + moreWeeks * metadata.more_intense_week_effect
        + lessWeeks * metadata.less_intense_week_effect;

    const dailyAmount = (daily_usage_type || 'per_person') === 'per_person' ? daily_usage * (nb_users || 0) : daily_usage;
    const annualAmount = dailyAmount * weekly_usage * nbReelWeeks;
    if (!annualAmount || isNaN(annualAmount)) return null;

    return {
        annualAmount,
        perDayAverage: annualAmount / 365,
        isTimeBased,
    };
}

function UsageForm({
    usageForm,
    equipmentType,
    equipmentTechnicalData,
    waterValueUnit,
    nbEquipments,
    buildingId,
    buildingData,
    functionalUnits,
    isEditing,
    singleEquipmentView,
    setSingleEquipmentView,
    showUsageDetails,
    toggleUsageDetails,
    onUsageChange,
    onUserCategoryChange,
    onSubmit,
}) {
    const [showIntenseWeeks, setShowIntenseWeeks] = useState(
        !!(usageForm.usage_data.less_intense_weeks || usageForm.usage_data.more_intense_weeks)
    );
    const [editingHotWater, setEditingHotWater] = useState(false);

    // Repli par défaut à chaque changement d'usage édité (sauf si l'usage a déjà
    // des semaines intenses/réduites renseignées, qu'on laisse alors visibles).
    useEffect(() => {
        setShowIntenseWeeks(!!(usageForm.usage_data.less_intense_weeks || usageForm.usage_data.more_intense_weeks));
        setEditingHotWater(false);
    }, [usageForm._id]);

    const isTotalUsage = usageForm.usage_data.daily_usage_type === 'total';
    const usersPerEquipment = nbEquipments > 0
        ? usageForm.usage_data.nb_users / nbEquipments
        : usageForm.usage_data.nb_users;
    const dailyUsagePerEquipment = nbEquipments > 0
        ? usageForm.usage_data.daily_usage / nbEquipments
        : usageForm.usage_data.daily_usage;

    const isTimeBased = waterValueUnit ? waterValueUnit === 'L/min' : TIME_BASED_TYPES.includes(equipmentType);
    const usageStats = computeUsageStats(isTimeBased, usageForm);
    // Part d'eau chaude retenue : celle de l'usage, sinon héritée de l'équipement (formulaire en cours).
    const hotWater = getHotWaterRatioWithSource({ usage: usageForm, equipmentType, technicalData: equipmentTechnicalData });
    const hotWaterNotConnected = hotWater.source === 'not_connected';

    return (
        <div className="audit-usage-form-container">
            <div className="usage-form-header">
                <h2>{isEditing ? 'Modifier un usage' : 'Ajouter un usage'}</h2>
                <FunctionalUnitTag
                    value={usageForm.functional_unit}
                    functionalUnits={functionalUnits}
                    onChange={(newValue) => onUsageChange({ target: { name: 'functional_unit', value: newValue } })}
                    variant="usage"
                />
            </div>

            <div className="audit-usage-form">

                {/* ── Étape 1 : Catégorie ── */}
                <div className="usage-step">
                    <div className="usage-step-title">
                        <span className="usage-step-badge">1</span>
                        <span>Catégorie</span>
                    </div>
                    <UsageCategoryPicker
                        categoryId={usageForm.usage_category_id}
                        subcategoryId={usageForm.usage_subcategory_id}
                        buildingId={buildingId}
                        buildingType={buildingData?.type_building}
                        onChange={(categoryId, subcategoryId, subcategoryLabel) => {
                            onUsageChange({ target: { name: 'usage_category_id', value: categoryId } });
                            onUsageChange({ target: { name: 'usage_subcategory_id', value: subcategoryId } });
                            // type_usage n'est plus qu'un aperçu client — le serveur le
                            // re-dérive systématiquement depuis usage_subcategory_id à
                            // l'écriture, mais on le garde synchronisé ici pour que la
                            // logique existante (ex. affichage dans UsageList) continue de
                            // fonctionner sans changement.
                            onUsageChange({ target: { name: 'type_usage', value: subcategoryLabel || '' } });
                        }}
                    />
                </div>

                {/* ── Étape 2 : Utilisation ── */}
                <div className="usage-step">
                    <div className="usage-step-title">
                        <span className="usage-step-badge">2</span>
                        <span>Utilisation</span>
                    </div>

                    {nbEquipments > 1 && (
                        <div className="alert-warning" style={{ display: 'flex', alignItems: 'center', backgroundColor: '#FFF3CD', color: '#856404', border: '1px solid #FFEeba', padding: '10px', borderRadius: '6px', marginBottom: '16px' }}>
                            <FaExclamationTriangle style={{ marginRight: '8px' }} />
                            <span>Attention, actuellement il y a {nbEquipments} équipements de ce type, pour {usageForm.usage_data.nb_users} utilisateurs répartis sur ces équipements. Vous pouvez saisir les données à l'échelle d'un équipement individuel.</span>
                        </div>
                    )}
                    {nbEquipments > 1 && (
                        <div className="form-group-inline-equip" style={{ marginBottom: '16px' }}>
                            <label>
                                <input type="checkbox" checked={singleEquipmentView} onChange={(e) => setSingleEquipmentView(e.target.checked)} />
                                {singleEquipmentView ? "Vision à l'échelle d'un seul équipement" : `Vision sur les ${nbEquipments} équipements`}
                            </label>
                        </div>
                    )}

                    {/* Nombre d'utilisateurs */}
                    <div className="form-group-inline-equip">
                        <label htmlFor="nb_users">{singleEquipmentView ? "Nombre d'utilisateurs par équipement" : "Nombre d'utilisateurs"}</label>
                        <input
                            type="number" id="nb_users" name="nb_users"
                            value={singleEquipmentView ? usersPerEquipment : usageForm.usage_data.nb_users}
                            onChange={(e) => {
                                const v = parseFloat(e.target.value);
                                onUsageChange({ target: { name: 'nb_users', value: singleEquipmentView ? v * nbEquipments : v } });
                            }}
                            required
                        />
                    </div>

                    {/* Catégorie d'utilisateur */}
                    <div className="form-group-inline-equip">
                        <label htmlFor="user_category">Catégorie d'utilisateur (optionnel)</label>
                        <select id="user_category" name="user_category" value={usageForm.user_name ?? 'none'} onChange={onUserCategoryChange}>
                            <option value="none">Aucun usager sélectionné</option>
                            {buildingData?.building_users?.map((user) => (
                                <option key={user.user_id} value={user.name} data-user-id={user.user_id}>{user.name}</option>
                            ))}
                        </select>
                    </div>

                    {isTotalUsage && nbEquipments > 1 && (
                        <div className="alert-warning" style={{ backgroundColor: '#FFF3CD', color: '#856404', border: '1px solid #FFEeba', padding: '8px', borderRadius: '6px', marginBottom: '8px', fontSize: '0.9em' }}>
                            {singleEquipmentView
                                ? <span>⚠️ Attention, il s'agit de la donnée d'usage quotidien pour <strong>un seul équipement</strong> sur les {nbEquipments} équipements.</span>
                                : <span>⚠️ Attention, la donnée d'usage quotidien est répartie sur les {nbEquipments} équipements.</span>
                            }
                        </div>
                    )}

                    {/* Usage quotidien */}
                    <div className="usage-daily-container">
                        <label htmlFor="daily_usage">Usage quotidien ({isTimeBased ? 'min/jour' : 'cycle/jour'})</label>
                        <div className="usage-daily-inputs">
                            <input
                                type="number" id="daily_usage" name="daily_usage"
                                value={isTotalUsage && singleEquipmentView ? dailyUsagePerEquipment : usageForm.usage_data.daily_usage}
                                onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    onUsageChange({ target: { name: 'daily_usage', value: isTotalUsage && singleEquipmentView ? v * nbEquipments : v } });
                                }}
                                required
                            />
                            <select
                                id="daily_usage_type" name="daily_usage_type"
                                value={usageForm.usage_data.daily_usage_type}
                                onChange={(e) => onUsageChange({ target: { name: 'daily_usage_type', value: e.target.value } })}
                                required
                            >
                                <option value="per_person">Par personne</option>
                                <option value="total">Total</option>
                            </select>
                        </div>
                    </div>

                    {!usageForm.is_known_volume && (
                        <>
                            <div className="form-group-inline-equip">
                                <label htmlFor="weekly_usage">Usage hebdomadaire (j/sem)</label>
                                <input type="number" id="weekly_usage" name="weekly_usage" value={usageForm.usage_data.weekly_usage} onChange={(e) => onUsageChange({ target: { name: 'weekly_usage', value: parseFloat(e.target.value) } })} required />
                            </div>
                            <div className="form-group-inline-equip">
                                <label htmlFor="yearly_usage">Usage annuel (sem/an)</label>
                                <input type="number" id="yearly_usage" name="yearly_usage" value={usageForm.usage_data.yearly_usage} onChange={(e) => onUsageChange({ target: { name: 'yearly_usage', value: parseFloat(e.target.value) } })} required />
                            </div>

                            <button type="button" className="usage-mini-toggle" onClick={() => setShowIntenseWeeks((p) => !p)}>
                                <span className={`arrow-icon ${showIntenseWeeks ? 'down' : 'right'}`} />
                                {showIntenseWeeks ? "Masquer les semaines d'activité intense/réduite" : "Semaines d'activité intense / réduite"}
                            </button>

                            {showIntenseWeeks && (
                                <>
                                    <div className="form-group-inline-equip">
                                        <label htmlFor="less_intense_weeks">Semaines moins intenses</label>
                                        <input type="number" id="less_intense_weeks" name="less_intense_weeks" value={usageForm.usage_data.less_intense_weeks} onChange={(e) => onUsageChange({ target: { name: 'less_intense_weeks', value: parseFloat(e.target.value) } })} required />
                                    </div>
                                    <div className="form-group-inline-equip">
                                        <label htmlFor="more_intense_weeks">Semaines plus intenses</label>
                                        <input type="number" id="more_intense_weeks" name="more_intense_weeks" value={usageForm.usage_data.more_intense_weeks} onChange={(e) => onUsageChange({ target: { name: 'more_intense_weeks', value: parseFloat(e.target.value) } })} required />
                                    </div>
                                </>
                            )}

                            {usageStats && (
                                <div className="usage-live-calc">
                                    {usageStats.isTimeBased
                                        ? <>≈ {Math.round(usageStats.annualAmount / 60)} h/an, soit {usageStats.perDayAverage.toFixed(1)} min/jour en moyenne</>
                                        : <>≈ {Math.round(usageStats.annualAmount)} cycles/an, soit {usageStats.perDayAverage.toFixed(1)} cycle/jour en moyenne</>}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ── Étape 3 : Eau ── */}
                <div className="usage-step">
                    <div className="usage-step-title">
                        <span className="usage-step-badge">3</span>
                        <span>Eau</span>
                    </div>

                    <div className="form-group-inline-equip">
                        <label>Eau en entrée</label>
                        <select name="water_quality_needed" value={usageForm.water_quality_needed} onChange={onUsageChange} required>
                            <option value="drinkable">Eau potable nécessaire</option>
                            <option value="non_drinkable">Eau non potable possible</option>
                        </select>
                    </div>

                    <div className="form-group-inline">
                        <label>Part d'eau chaude</label>
                        <div className="usage-hwr-block">
                        {editingHotWater && !hotWaterNotConnected ? (
                            <HotWaterRatioInput
                                value={hotWater.value}
                                equipmentType={equipmentType}
                                onChange={(newValue) => onUsageChange({ target: { name: 'hot_water_ratio', value: newValue, type: 'number' } })}
                            />
                        ) : (
                            <div className="usage-hwr-collapsed">
                                <span className="usage-hwr-collapsed-value">{hotWater.value} %</span>
                                <span className="usage-hwr-source">{HOT_WATER_SOURCE_LABELS[hotWater.source]}</span>
                                {!hotWaterNotConnected && (
                                    <button type="button" className="usage-hwr-edit-link" onClick={() => setEditingHotWater(true)}>
                                        Modifier
                                    </button>
                                )}
                            </div>
                        )}
                        {!hotWaterNotConnected && usageForm.hot_water_ratio != null && (
                            <button
                                type="button"
                                className="usage-hwr-edit-link"
                                onClick={() => {
                                    onUsageChange({ target: { name: 'hot_water_ratio', value: null } });
                                    setEditingHotWater(false);
                                }}
                            >
                                Reprendre la part de l'équipement
                            </button>
                        )}
                        </div>
                    </div>
                </div>

                {/* ── Étape 4 : Hypothèses et sources ── */}
                <div className="usage-step">
                    <div className="usage-step-title">
                        <span className="usage-step-badge">4</span>
                        <span>Hypothèses et sources</span>
                    </div>
                    {usageForm.distribution_id ? (
                        <p className="usage-notes-hint">
                            Usage issu d'un lot de répartition : ses hypothèses se notent sur le lot ou le profil
                            d'usagers (une note ici serait perdue au prochain recalcul du lot).
                        </p>
                    ) : (
                        <textarea
                            className="usage-notes-input" name="notes" rows={3}
                            value={usageForm.notes ?? ''}
                            placeholder="Ex. : 4 j/sem, 2 cycles/jour — dit par le gérant (visite du 12/10)"
                            onChange={onUsageChange}
                        />
                    )}
                </div>

                {/* ── Étape 5 : Informations complémentaires (repliable) ── */}
                <div className="usage-step">
                    <button type="button" className="usage-step-title usage-step-title--toggle" onClick={toggleUsageDetails}>
                        <span className="usage-step-badge">5</span>
                        <span>Informations complémentaires</span>
                        <span className={`arrow-icon ${showUsageDetails ? 'down' : 'right'}`} />
                    </button>

                    {showUsageDetails && (
                        <div className="usage-step-body">
                            {usageHasSurface(usageForm) && (
                                <div className="form-group-inline-equip">
                                    <label htmlFor="surface_area">Surface (m²)</label>
                                    <input type="number" id="surface_area" name="surface_area" value={usageForm.surface_area || ''} onChange={onUsageChange} required min={1} />
                                </div>
                            )}

                            <div className="form-group-inline-equip">
                                <label>Eau en sortie</label>
                                <select name="water_quality_out" value={usageForm.water_quality_out} onChange={onUsageChange} required>
                                    <option value="drinkable">Eau potable en sortie</option>
                                    <option value="non_drinkable">Eau non potable en sortie</option>
                                </select>
                            </div>

                            <div className="form-group-inline">
                                <label>Économies possibles</label>
                                <ToggleButton name="possible_savings" value={usageForm.possible_savings} onChange={(e) => onUsageChange({ target: { name: 'possible_savings', value: e.target.checked } })} context="usage" />
                            </div>

                            <div className="form-group-inline">
                                <label>Connaissance du volume annuel</label>
                                <ToggleButton name="is_known_volume" value={usageForm.is_known_volume} onChange={(e) => onUsageChange({ target: { name: 'is_known_volume', value: e.target.checked } })} context="usage" />
                            </div>

                            {usageForm.is_known_volume && (
                                <div className="form-group-inline-equip">
                                    <label htmlFor="annual_volume">Volume annuel (m³)</label>
                                    <input type="number" id="annual_volume" name="annual_volume" value={usageForm.annual_volume || ''} onChange={onUsageChange} required min={1} />
                                </div>
                            )}

                            {!usageForm.is_known_volume && equipmentType === 'machine' && (
                                <>
                                    <div className="form-group-inline-equip">
                                        <label htmlFor="cycle_duration">Durée du cycle (min)</label>
                                        <input type="number" id="cycle_duration" name="cycle_duration" value={usageForm.usage_data.cycle_duration} onChange={(e) => onUsageChange({ target: { name: 'cycle_duration', value: parseFloat(e.target.value) } })} required />
                                    </div>
                                    <div className="form-group-inline-equip">
                                        <label htmlFor="daily_utilisation">Utilisation quotidienne (min)</label>
                                        <input type="number" id="daily_utilisation" name="daily_utilisation" value={usageForm.usage_data.daily_utilisation} onChange={(e) => onUsageChange({ target: { name: 'daily_utilisation', value: parseFloat(e.target.value) } })} required />
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="usage-form-footer">
                <button className="save-button" onClick={onSubmit}>
                    {isEditing ? 'Enregistrer la modification' : "Enregistrer l'usage"}
                </button>
            </div>
        </div>
    );
}

export default UsageForm;
